// SPDX-License-Identifier: Elastic-2.0
/**
 * useSettings — TanStack Query hooks for the `/me/preferences` surface
 * consumed by the Settings page (T-028).
 *
 * F-300 has NOT yet exposed a `/me/preferences` endpoint (verified by
 * inspecting `web/src/api/gateway/services/` — only `MeResponse`
 * exists, no preferences read/write). To unblock T-028 (and avoid
 * pinning Group 7 on a Lane A backfill), this module ships a
 * **localStorage-backed stub** that conforms to the same shape the real
 * endpoint will return. When F-300 lands `GET /me/preferences` +
 * `PATCH /me/preferences`, the implementation behind `fetchPreferences`
 * / `savePreferences` swaps to the generated client — call-sites
 * (PrivacySection, PairedDevicesSection) stay untouched.
 *
 * Tracking flag: `__PREFERENCES_BACKEND__` exported below — search for
 * it in the codebase to find the swap points.
 *
 * Data shape:
 *   - `preferences.telemetry|crash|improve` — three booleans matching
 *     the A4 privacy step + F-114 privacy defaults. Seeded from the
 *     onboarding payload (`useOnboardingStore.privacy`) on first read
 *     when no localStorage entry exists; this keeps the user's A4
 *     choices visible in Settings without a network round-trip.
 *   - `pairedDevices` — list of `{ hostname, last_seen, paired_at }`.
 *     MVP populates with a single synthetic "This Pi" entry (the local
 *     Mirlai box) since F-300 has no paired-devices endpoint yet. The
 *     hostname is read from `navigator` / window heuristics with a
 *     "mirlai" fallback; `last_seen` is `now`, `paired_at` is whatever
 *     the local store first observed.
 *
 * The hooks expose a familiar TanStack-Query shape so the eventual
 * swap to a real network call is mechanical.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

/** Sentinel flag — flip to `"network"` once F-300 ships `/me/preferences`. */
export const __PREFERENCES_BACKEND__: "localStorage" | "network" = "localStorage";

/** Query key for the `/me/preferences` GET. */
export const PREFERENCES_QUERY_KEY = ["me", "preferences"] as const;
/** Query key for the paired-devices list. */
export const PAIRED_DEVICES_QUERY_KEY = ["me", "paired-devices"] as const;

/** localStorage key for the preferences stub. */
export const PREFERENCES_STORAGE_KEY = "mirlai_me_preferences";
/** localStorage key for the synthetic paired-devices list. */
export const PAIRED_DEVICES_STORAGE_KEY = "mirlai_me_paired_devices";

/**
 * The three privacy toggles surfaced in `PrivacySection`. Matches the
 * `PrivacyPayload` shape from `useOnboardingStore` so the A4 → Settings
 * carryover is a structural copy.
 */
export interface Preferences {
  telemetry: boolean;
  crash: boolean;
  improve: boolean;
}

/** Default preferences when nothing is stored — all off per F-114. */
export const DEFAULT_PREFERENCES: Preferences = {
  telemetry: false,
  crash: false,
  improve: false,
};

/** Per-device paired-device row. */
export interface PairedDevice {
  /** Hostname displayed in the section row. */
  hostname: string;
  /** ISO timestamp string of the last heartbeat. */
  last_seen: string;
  /** ISO timestamp string of the original pairing. */
  paired_at: string;
}

// ── localStorage helpers ───────────────────────────────────────────────

function readStorage<T>(key: string): T | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: unknown): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / private mode — ignore.
  }
}

/**
 * Best-effort hostname guess. The browser doesn't expose the device
 * hostname; we synthesise something readable so the row isn't empty.
 * The real implementation will read `MeResponse.hostname` from F-300.
 */
function resolveHostname(): string {
  if (typeof window !== "undefined" && typeof window.location?.hostname === "string") {
    const h = window.location.hostname;
    // Skip generic dev/loopback values that wouldn't be informative.
    if (h && h !== "localhost" && h !== "127.0.0.1" && h !== "0.0.0.0") {
      return h;
    }
  }
  return "mirlai.local";
}

/**
 * Seed preferences from the onboarding store if available. We import
 * lazily inside the function so unit tests that boot just the settings
 * surface don't pull onboarding state into the bundle.
 */
function seedFromOnboarding(): Preferences {
  try {
    // The onboarding store self-hydrates from localStorage at module
    // import; reading its persisted slice directly is cheap and lets
    // first-time Settings visitors see the choices they made in A4.
    const raw = readStorage<{ privacy?: Partial<Preferences> }>("onboarding-state");
    if (raw && raw.privacy && typeof raw.privacy === "object") {
      return {
        telemetry: Boolean(raw.privacy.telemetry),
        crash: Boolean(raw.privacy.crash),
        improve: Boolean(raw.privacy.improve),
      };
    }
  } catch {
    // Ignore — fall through to defaults.
  }
  return { ...DEFAULT_PREFERENCES };
}

/** Read the persisted preferences blob, seeding from onboarding if absent. */
export function readPreferencesFromStorage(): Preferences {
  const stored = readStorage<Partial<Preferences>>(PREFERENCES_STORAGE_KEY);
  if (stored && typeof stored === "object") {
    return {
      telemetry: Boolean(stored.telemetry),
      crash: Boolean(stored.crash),
      improve: Boolean(stored.improve),
    };
  }
  return seedFromOnboarding();
}

/** Read the synthetic paired-devices list, creating the "This Pi" row if absent. */
export function readPairedDevicesFromStorage(): PairedDevice[] {
  const stored = readStorage<PairedDevice[]>(PAIRED_DEVICES_STORAGE_KEY);
  const now = new Date().toISOString();
  if (Array.isArray(stored) && stored.length > 0) {
    // Bump last_seen to "now" on every read — mirrors the lively
    // heartbeat the real endpoint will return.
    return stored.map((d, idx) =>
      idx === 0 ? { ...d, last_seen: now } : d,
    );
  }
  const synthetic: PairedDevice = {
    hostname: resolveHostname(),
    last_seen: now,
    paired_at: now,
  };
  writeStorage(PAIRED_DEVICES_STORAGE_KEY, [synthetic]);
  return [synthetic];
}

// ── Async wrappers (drop-in for the future network endpoints) ──────────

/** Promise-shaped reader so the swap to `fetch` later is a one-liner. */
export async function fetchPreferences(): Promise<Preferences> {
  return Promise.resolve(readPreferencesFromStorage());
}

/** Promise-shaped writer mirroring the future `PATCH /me/preferences`. */
export async function savePreferences(next: Preferences): Promise<Preferences> {
  writeStorage(PREFERENCES_STORAGE_KEY, next);
  return Promise.resolve(next);
}

/** Promise-shaped paired-devices reader. */
export async function fetchPairedDevices(): Promise<PairedDevice[]> {
  return Promise.resolve(readPairedDevicesFromStorage());
}

/**
 * F-342 opt-out: ask the gateway to delete this install's historical
 * telemetry from PostHog.
 *
 * Best-effort + non-blocking by contract — the gateway returns
 * ``202 Accepted`` and runs the actual PostHog delete in the
 * background, so this never needs to await a slow upstream. Any
 * network / auth failure is swallowed (logged): the opt-out itself
 * (stopping further sending) is the local preference write, which has
 * already happened; the historical delete is a best-effort follow-up,
 * exactly as the functional spec's Failure-scenarios row describes
 * ("stopped; deletion queued").
 *
 * Hand-rolled fetch (not the generated gateway client) because the
 * ``DELETE /telemetry/install`` route post-dates the committed
 * gateway OpenAPI snapshot; swap to the generated method after the
 * next ``npm run gen:api:gateway``. The endpoint takes no id — the
 * gateway deletes its own install's data.
 */
export async function requestTelemetryDelete(): Promise<void> {
  if (typeof fetch === "undefined") return;
  const base =
    (import.meta.env.VITE_GATEWAY_BASE_URL as string | undefined) ??
    "http://localhost:8200";
  let token: string | null = null;
  try {
    // Lazy import to keep the auth store out of settings-only test bundles.
    const { useAuth } = await import("../../api/auth-store");
    token = useAuth.getState().getActiveJwt();
  } catch {
    token = null;
  }
  try {
    await fetch(`${base.replace(/\/$/, "")}/api/mirlai/v1/telemetry/install`, {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  } catch {
    // Non-blocking: the local off-state already stopped sending.
  }
}

// ── React hooks ────────────────────────────────────────────────────────

/**
 * `useSettings()` — query + mutation pair for the privacy preferences.
 *
 * Returns:
 *   - `query`     — TanStack `UseQueryResult<Preferences>`. `data`
 *                   resolves synchronously in practice (localStorage
 *                   stub) but consumers should still gate on
 *                   `query.data !== undefined` so the swap to the real
 *                   endpoint doesn't require call-site changes.
 *   - `mutation`  — TanStack `UseMutationResult` that POSTs the next
 *                   preferences blob; on success it `setQueryData`s the
 *                   cache so the form's "dirty" flag flips back to
 *                   clean without a refetch.
 */
export function useSettings(): {
  query: UseQueryResult<Preferences, Error>;
  mutation: UseMutationResult<Preferences, Error, Preferences>;
} {
  const queryClient = useQueryClient();

  const query = useQuery<Preferences, Error>({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: fetchPreferences,
    staleTime: 60_000,
  });

  const mutation = useMutation<Preferences, Error, Preferences>({
    mutationFn: savePreferences,
    onSuccess: (next) => {
      queryClient.setQueryData<Preferences>(PREFERENCES_QUERY_KEY, next);
    },
  });

  return { query, mutation };
}

/** `usePairedDevices()` — synthetic list of `{ hostname, last_seen, paired_at }`. */
export function usePairedDevices(): UseQueryResult<PairedDevice[], Error> {
  return useQuery<PairedDevice[], Error>({
    queryKey: PAIRED_DEVICES_QUERY_KEY,
    queryFn: fetchPairedDevices,
    staleTime: 60_000,
  });
}

/** Exported for unit tests — direct host resolution check. */
export const __internal = {
  resolveHostname,
  seedFromOnboarding,
};
